import { isPlayedResult, pointsFor, type Colour } from '../model/types.js';
import { maxWeightMatching, type WeightedEdge } from '../matching/blossom.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

/**
 * US Chess Swiss pairings (Official Rules of Chess, 7th ed., rules 27-29).
 *
 * The US rules leave the director discretion in places; this engine is a
 * deterministic reading of them:
 * - rank by score, then rating (29A); pair score groups from the top (29B);
 * - an odd group drops its lowest-rated player (not unrated, when possible),
 *   who is paired with the highest-rated player he can meet in the next
 *   group (29D);
 * - upper half against lower half in consecutive order (29C1);
 * - transpositions within the lower half avoid rematches (29C2) and, within
 *   the 80-point rule (29E5a), give more players their due colour; an
 *   interchange between the halves is used only if no transposition does as
 *   well (29E5e);
 * - the full-point bye goes to the lowest-rated player of the lowest score
 *   group who has not had a bye or a no-show win (28L), avoiding players
 *   who took a half-point bye (28L4);
 * - colours per 29E (equalisation before alternation, unplayed games ignored).
 */

interface UPlayer {
  readonly p: PairingPlayer;
  readonly id: number;
  readonly rating: number;
  readonly score: number;
  readonly opponents: ReadonlySet<number>;
  readonly colours: readonly Colour[];
  readonly hadBye: boolean;
  readonly hadHalfBye: boolean;
}

/** Due colour (29E1-29E3): equalising colour first, then alternation. */
function due(u: UPlayer): { colour: Colour | null; imbalance: number } {
  let diff = 0;
  for (const c of u.colours) diff += c === 'w' ? 1 : -1;
  if (diff > 0) return { colour: 'b', imbalance: diff };
  if (diff < 0) return { colour: 'w', imbalance: -diff };
  const last = u.colours[u.colours.length - 1];
  return { colour: last === undefined ? null : last === 'w' ? 'b' : 'w', imbalance: 0 };
}

/** Colour for the higher-ranked player `a` against `b` (29E4 priorities). */
export function uscfColours(
  a: UPlayer,
  b: UPlayer,
  aIsFirstBoardSide: boolean,
  initial: Colour,
): Colour {
  const da = due(a);
  const db = due(b);
  if (da.colour !== null && db.colour !== null && da.colour !== db.colour) return da.colour;
  if (da.colour !== null && db.colour === null) return da.colour;
  if (da.colour === null && db.colour !== null) return db.colour === 'w' ? 'b' : 'w';
  if (da.colour === null || db.colour === null) {
    return aIsFirstBoardSide ? initial : initial === 'w' ? 'b' : 'w';
  }
  // Both due the same colour.
  const give = (winner: 'a' | 'b'): Colour =>
    winner === 'a' ? (da.colour as Colour) : (db.colour as Colour) === 'w' ? 'b' : 'w';
  if (da.imbalance > 0 !== db.imbalance > 0) return give(da.imbalance > 0 ? 'a' : 'b');
  if (da.imbalance !== db.imbalance) return give(da.imbalance > db.imbalance ? 'a' : 'b');
  const la = a.colours[a.colours.length - 1];
  const lb = b.colours[b.colours.length - 1];
  if (la !== undefined && lb !== undefined && la !== lb) return la === 'w' ? 'b' : 'w';
  for (let i = 1; i <= Math.min(a.colours.length, b.colours.length); i++) {
    const ca = a.colours[a.colours.length - i] as Colour;
    const cb = b.colours[b.colours.length - i] as Colour;
    if (ca !== cb) return ca === 'w' ? 'b' : 'w';
  }
  return da.colour;
}

function build(req: PairingRequest): UPlayer[] {
  const ps = req.pointSystem;
  const forbidden = new Map<number, Set<number>>();
  for (const [x, y] of req.forbiddenPairs ?? []) {
    if (!forbidden.has(x)) forbidden.set(x, new Set());
    if (!forbidden.has(y)) forbidden.set(y, new Set());
    (forbidden.get(x) as Set<number>).add(y);
    (forbidden.get(y) as Set<number>).add(x);
  }
  return req.players
    .filter((p) => p.available)
    .map((p) => {
      const opponents = new Set<number>(forbidden.get(p.id) ?? []);
      const colours: Colour[] = [];
      let score = p.acceleration ?? 0;
      let hadBye = false;
      let hadHalfBye = false;
      for (const e of p.history) {
        score += pointsFor(e.result, ps);
        // 27A1: forfeited non-appearance games may be re-paired.
        if (e.opponent !== null && isPlayedResult(e.result)) opponents.add(e.opponent);
        if (e.colour !== null && isPlayedResult(e.result)) colours.push(e.colour);
        if (e.result === 'U' || e.result === 'F' || e.result === '+') hadBye = true;
        if (e.result === 'H') hadHalfBye = true;
      }
      return { p, id: p.id, rating: p.rating, score, opponents, colours, hadBye, hadHalfBye };
    })
    .sort((a, b) => b.score - a.score || b.rating - a.rating || a.id - b.id);
}

const canMeet = (a: UPlayer, b: UPlayer): boolean =>
  !a.opponents.has(b.id) && !b.opponents.has(a.id);

/** Can this set of players be paired completely without rematches? */
function pairable(players: readonly UPlayer[]): boolean {
  if (players.length % 2 === 1) return false;
  const edges: WeightedEdge[] = [];
  for (let i = 0; i < players.length; i++) {
    for (let j = i + 1; j < players.length; j++) {
      if (canMeet(players[i] as UPlayer, players[j] as UPlayer)) edges.push({ u: i, v: j, w: 1n });
    }
  }
  const mate = maxWeightMatching(players.length, edges, { maxCardinality: true });
  return Array.from(mate).every((m) => m >= 0);
}

function colourConflicts(pairs: readonly [UPlayer, UPlayer][]): number {
  let n = 0;
  for (const [a, b] of pairs) {
    const da = due(a);
    const db = due(b);
    if (da.colour !== null && da.colour === db.colour)
      n += 1 + Math.min(da.imbalance, db.imbalance);
  }
  return n;
}

/** Rematch-free assignments of `up` to `low`, in transposition order (29C2). */
function* assignments(
  up: readonly UPlayer[],
  low: readonly UPlayer[],
): Generator<[UPlayer, UPlayer][]> {
  const used = new Array<boolean>(low.length).fill(false);
  const cur: [UPlayer, UPlayer][] = [];
  function* rec(i: number): Generator<[UPlayer, UPlayer][]> {
    if (i === up.length) {
      yield [...cur];
      return;
    }
    const a = up[i] as UPlayer;
    for (let j = 0; j < low.length; j++) {
      if (used[j]) continue;
      const b = low[j] as UPlayer;
      if (!canMeet(a, b)) continue;
      used[j] = true;
      cur.push([a, b]);
      yield* rec(i + 1);
      cur.pop();
      used[j] = false;
    }
  }
  yield* rec(0);
}

/** 29E5a: colour-motivated transpositions keep each opponent within 80 points of the baseline one. */
function within80(
  cand: readonly [UPlayer, UPlayer][],
  base: readonly [UPlayer, UPlayer][],
): boolean {
  return cand.every(
    ([, b], k) => Math.abs(b.rating - (base[k] as [UPlayer, UPlayer])[1].rating) <= 80,
  );
}

const rankSort = (a: UPlayer, b: UPlayer): number =>
  b.score - a.score || b.rating - a.rating || a.id - b.id;

/** Best pairing of halves: the first rematch-free one, improved for colours within 80 points. */
function bestOf(
  up: readonly UPlayer[],
  low: readonly UPlayer[],
  budget: number,
): { pairs: [UPlayer, UPlayer][]; conflicts: number } | null {
  let base: [UPlayer, UPlayer][] | null = null;
  let best: { pairs: [UPlayer, UPlayer][]; conflicts: number } | null = null;
  let n = 0;
  for (const cand of assignments(up, low)) {
    if (base === null) {
      base = cand;
      best = { pairs: cand, conflicts: colourConflicts(cand) };
      if (best.conflicts === 0) break;
      continue;
    }
    if (++n > budget) break;
    if (!within80(cand, base)) continue;
    const c = colourConflicts(cand);
    if (c < (best as { conflicts: number }).conflicts) {
      best = { pairs: cand, conflicts: c };
      if (c === 0) break;
    }
  }
  return best;
}

/**
 * Pair one even group: upper half against lower half with transpositions
 * (29C1-29C2); interchanges between the halves only when transpositions
 * cannot avoid rematches, or give more due colours within 80 points
 * (29E5d-e). Returns null if the group cannot be paired without rematches.
 */
function pairGroup(group: readonly UPlayer[], budget = 3000): [UPlayer, UPlayer][] | null {
  const half = group.length / 2;
  const upper = group.slice(0, half);
  const lower = group.slice(half);
  let best = bestOf(upper, lower, budget);
  if (best !== null && best.conflicts === 0) return best.pairs;
  for (let k = 1; k <= Math.min(2, half); k++) {
    const outOfUpper = upper.slice(half - k);
    const intoUpper = lower.slice(0, k);
    const forColours = best !== null;
    if (
      forColours &&
      !outOfUpper.every((u, i) => Math.abs(u.rating - (intoUpper[i] as UPlayer).rating) <= 80)
    )
      continue;
    const up = [...upper.slice(0, half - k), ...intoUpper].sort(rankSort);
    const low = [...outOfUpper, ...lower.slice(k)].sort(rankSort);
    const cand = bestOf(up, low, budget);
    if (cand !== null && (best === null || cand.conflicts < best.conflicts)) {
      best = cand;
      break;
    }
  }
  return best === null ? null : best.pairs;
}

export class UscfEngine implements PairingEngine {
  readonly id = 'uscf';
  readonly name = 'US Chess Swiss (rules 27-29)';

  pair(request: PairingRequest): PairingResult {
    const players = build(request);
    const notes: string[] = [];
    let pool = [...players];
    const byes: number[] = [];

    // 28L: the full-point bye.
    if (pool.length % 2 === 1) {
      const candidates = [...pool]
        .filter((u) => !u.hadBye)
        .sort(
          (a, b) =>
            a.score - b.score ||
            Number(a.hadHalfBye) - Number(b.hadHalfBye) ||
            (a.rating > 0 ? 0 : 1) - (b.rating > 0 ? 0 : 1) ||
            a.rating - b.rating ||
            b.id - a.id,
        );
      const choice = candidates.find((c) => pairable(pool.filter((u) => u !== c))) ?? candidates[0];
      if (choice === undefined) throw new PairingError('no player can receive the bye');
      byes.push(choice.id);
      pool = pool.filter((u) => u !== choice);
    }

    const pairs: [UPlayer, UPlayer][] = [];
    const scores = [...new Set(pool.map((u) => u.score))].sort((a, b) => b - a);
    let carried: UPlayer[] = [];
    for (let gi = 0; gi < scores.length; gi++) {
      const s = scores[gi] as number;
      const residents = pool.filter((u) => u.score === s);
      const rest = pool.filter((u) => u.score < s);
      // 29D: a dropped player meets the highest-rated player he can play here.
      let group = [...residents];
      for (const d of carried) {
        const others = [...group, ...rest, ...carried.filter((x) => x !== d)];
        const opp = group
          .filter((g) => canMeet(d, g))
          .sort((a, b) => b.rating - a.rating || a.id - b.id)
          .find((g) =>
            pairable(others.filter((x) => x !== g && !pairs.some(([p, q]) => p === x || q === x))),
          );
        if (opp !== undefined) {
          pairs.push([d, opp]);
          group = group.filter((x) => x !== opp);
        } else {
          group.push(d);
        }
      }
      carried = [];
      group.sort((a, b) => b.score - a.score || b.rating - a.rating || a.id - b.id);
      const last = gi === scores.length - 1;
      // Drop players (lowest-rated first) until the group pairs and the rest stays pairable.
      let attempt: [UPlayer, UPlayer][] | null = null;
      for (let drop = group.length % 2; drop <= group.length; drop += 2) {
        if (last && drop > 0) break;
        const byRating = [...group].sort(
          (a, b) =>
            (a.rating > 0 ? 0 : 1) - (b.rating > 0 ? 0 : 1) || a.rating - b.rating || b.id - a.id,
        );
        const dropped = byRating.slice(0, drop);
        const keep = group.filter((u) => !dropped.includes(u));
        if (!last && !pairable([...dropped, ...rest]) && rest.length + dropped.length > 0) {
          continue;
        }
        attempt = keep.length === 0 ? [] : pairGroup(keep);
        if (attempt !== null) {
          carried = dropped;
          break;
        }
      }
      if (attempt === null) {
        if (!last) {
          carried = group;
          continue;
        }
        throw new PairingError('the last score group cannot be paired without rematches');
      }
      pairs.push(...attempt);
    }
    if (carried.length > 0) throw new PairingError('players left unpaired');

    const rankOf = new Map(players.map((u, i) => [u.id, i]));
    const ordered = pairs
      .map(([a, b]) =>
        (rankOf.get(a.id) as number) < (rankOf.get(b.id) as number)
          ? ([a, b] as const)
          : ([b, a] as const),
      )
      .sort(([a1], [a2]) => (rankOf.get(a1.id) as number) - (rankOf.get(a2.id) as number));
    const out: Pair[] = ordered.map(([a, b], board) => {
      const c = uscfColours(a, b, board % 2 === 0, request.initialColour);
      return c === 'w' ? { white: a.id, black: b.id } : { white: b.id, black: a.id };
    });
    return { pairs: out, byes, notes };
  }
}
